import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import Button from 'components/Buttons';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { IRootReduxState } from 'types/reducers';
import { makeStyles } from '@material-ui/core/styles';

const useDarkBtnStyles = makeStyles(() => ({
  darkCancelBtn: {
    backgroundColor: (props: { isDarkMode: boolean }) => props.isDarkMode ? 'transparent !important' : undefined,
    color: (props: { isDarkMode: boolean }) => props.isDarkMode ? '#e4e7ec !important' : undefined,
    borderColor: (props: { isDarkMode: boolean }) => props.isDarkMode ? '#475569 !important' : undefined,
    '&:hover': {
      backgroundColor: (props: { isDarkMode: boolean }) => props.isDarkMode ? 'rgba(255,255,255,0.05) !important' : undefined,
      borderColor: (props: { isDarkMode: boolean }) => props.isDarkMode ? '#64748b !important' : undefined,
    },
  },
}));

interface Props extends DialogProps {
  message: string | JSX.Element,
  handleOk?: () => void,
  handleCancel?: () => void
}

const ConfirmationDialog: React.FC<Props> = ({ open, message, title = <FormattedMessage id="confirmation" defaultMessage="Please Confirm" />, handleOk, handleCancel, ...props }) => {
  const isDarkMode = useSelector((state: IRootReduxState) => state.app.user.theme === 'dark');
  const btnClasses = useDarkBtnStyles({ isDarkMode });
  const titleColor = isDarkMode ? '#e4e7ec' : '#111827';
  const messageColor = isDarkMode ? '#cbd5e1' : '#374151';
  const dialogBg = isDarkMode ? '#1e293b' : '#FFFFFF';
  const dividerColor = isDarkMode ? '#334155' : 'rgba(0, 0, 0, 0.12)';

  return (
    <Dialog
      {...props}
      open={open === true}
      onClose={() => handleCancel && handleCancel()}
      aria-labelledby="form-dialog-title"
      PaperProps={{ style: { borderRadius: 8, minWidth: 500, backgroundColor: dialogBg } }}
    >
      <DialogTitle id="form-dialog-title" disableTypography style={{ padding: '24px 28px 0' }}>
        <Typography style={{ fontFamily: '"Public Sans", sans-serif', fontSize: 17, fontWeight: 600, color: titleColor }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent style={{ padding: '20px 28px 8px' }}>
        <Divider style={{ marginBottom: 20, backgroundColor: dividerColor }} />
        <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <WarningRoundedIcon style={{ color: '#F59E0B', fontSize: 34, flexShrink: 0 }} />
          <Typography style={{ fontFamily: '"Public Sans", sans-serif', fontSize: 14, color: messageColor, lineHeight: 1.7 }}>
            {message}
          </Typography>
        </Box>
        <Divider style={{ marginTop: 20, backgroundColor: dividerColor }} />
      </DialogContent>

      <DialogActions style={{ padding: '14px 24px 20px', gap: 10 }}>
        <Button.NoButton className={btnClasses.darkCancelBtn} click={() => handleCancel && handleCancel()} />
        <Button.YesButton click={() => handleOk && handleOk()} />
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
